import React from "react";
import { Box, Typography } from "@mui/material";
import { CaregiverHandbookCard, HandbookSourceViewer } from "../../UI";
import colors from "../../../assets/theme/colors";
import HandbookFactEditSheet from "./HandbookFactEditSheet";
import { useHandbookSourceInteraction } from "./useHandbookSourceInteraction";

const feedbackSx = {
  mb: 0.75,
  px: 0.5,
  fontSize: "0.82rem",
  fontWeight: 500,
};

/**
 * Shared presentation and lifecycle boundary for source-routed Profile chapters.
 * Chapter-specific content stays in the resolved Profile chapter contract.
 */
const KnowledgeBackedProfileChapter = ({
  chapter,
  status = "ready",
  knowledgeFacts = [],
  canEditProfile = false,
  canViewDocuments = false,
  onAddCta,
  addCtaLabel,
  onNavigateToManager,
  presentationMode = "summary",
  showGroupHeadings = false,
  completionReceipt = null,
}) => {
  const interaction = useHandbookSourceInteraction({
    chapter,
    knowledgeFacts,
    canEditProfile,
    canViewDocuments,
    onNavigateToManager,
  });

  return (
    <Box aria-busy={status === "loading"}>
      {completionReceipt ? (
        <Box
          role="status"
          sx={{
            mb: 1,
            px: 1.2,
            py: 1,
            borderRadius: "12px",
            bgcolor: colors.semantic.sageMist,
            color: colors.semantic.ink,
          }}
        >
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 900 }}>
            {`${completionReceipt.addedCount} detail${completionReceipt.addedCount === 1 ? "" : "s"} added elsewhere in ${completionReceipt.childName || "this person"}’s Profile`}
          </Typography>
          {Object.entries(completionReceipt.chapterCounts || {}).map(([label, count]) => (
            <Typography key={label} sx={{ mt: 0.2, fontSize: "0.78rem", color: colors.semantic.sageDeep }}>
              {`${label} · ${count}`}
            </Typography>
          ))}
        </Box>
      ) : null}
      {interaction.refreshBanner ? (
        <Typography sx={{ ...feedbackSx, color: colors.semantic.sageDeep }}>
          {interaction.refreshBanner}
        </Typography>
      ) : null}
      {interaction.visibilityError ? (
        <Typography role="alert" sx={{ ...feedbackSx, fontWeight: 800, color: "#B3261E" }}>
          {interaction.visibilityError}
        </Typography>
      ) : null}
      {interaction.lifecycleError ? (
        <Typography role="alert" sx={{ ...feedbackSx, fontWeight: 800, color: "#B3261E" }}>
          {interaction.lifecycleError}
        </Typography>
      ) : null}
      <CaregiverHandbookCard
        chapter={interaction.resolvedChapter}
        status={status}
        onEntryAction={interaction.handleEntryAction}
        onEntryVisibilityAction={interaction.handleHideFromProfile}
        onEntryMarkOutdated={interaction.handleMarkOutdated}
        onEntryDelete={interaction.handleDelete}
        onAddCta={onAddCta}
        addCtaLabel={addCtaLabel}
        presentationMode={presentationMode}
        showGroupHeadings={showGroupHeadings}
      />
      <HandbookFactEditSheet {...interaction.editSheetProps} />
      <HandbookSourceViewer {...interaction.viewSourceProps} />
    </Box>
  );
};

export default KnowledgeBackedProfileChapter;
